<template>
   <div class="audio-player">
      <audio ref="audioEmbed" v-on:ended="handleAudioEnd">
         <source :src="audioFilePath" type="audio/mpeg">
      </audio>
      <canvas v-if="!useFallback" class="audio-player-canvas" ref="audioCanvas" height="50" :width="canvasWidth"></canvas>
      <div v-if="useFallback" class="visualizer">
         <img class="visualizer-still" :class="{hidden: isPlaying}" :src="$store.state.isPortrait ? visualizerStillSrc : visualizerStillDarkSrc"
              ref="visualizerStill" alt=""/>
         <img class="visualizer-gif" :class="{active: isPlaying}" :src="visualizerGifSrc" ref="visualizerGif" alt=""/>
      </div>
      <button class="audio-play-pause" v-on:click="handleListenClick">{{ playText }}</button>
   </div>
</template>

<script>

    import {bus, audioContext, isIosDevice, isSafari} from './../consts'

    export default {
        name: 'AudioPlayer',
        props: [
            'filename',
            'quoteActive',
            'quoteIdx'
        ],
        data() {
            return {
                isPlaying: false,
                rafId: undefined,
                canvas: null,
                canvasCtx: null,
                analyser: null,
                source: null,
                fbc_array: [],
                barCount: 60,
                barWidth: 2,
                barSpace: 4,
                barHeight: 0,
                barIconColor: '#797979',
                barAnimatedColor: '#FFFFFF',
                audioCtx: audioContext,
                mirrorAnalyser: true,
                iconBarHeights: [5, 11, 9, 15, 12, 18, 26, 15, 9, 5],
                useFallback: false,
                audioTimeout: null,
                playPromise: null
            }
        },
        computed: {
            audioFilePath() {
                return require('./../assets/audio/' + this.filename);
            },
            visualizerStillSrc() {
                return require('../assets/images/sound-bar-still.png');
            },
            visualizerStillDarkSrc() {
                let pathMod = this.quoteActive ? '' : '-dark';
                return require('../assets/images/sound-bar-still' + pathMod + '.png');
            },
            visualizerGifSrc() {
                return require('../assets/images/sound-bar.gif');
            },
            playText() {
                return this.isPlaying ? 'pause' : 'listen';
            },
            barXBase() {
                return this.barWidth + this.barSpace;
            },
            canvasWidth() {
                return this.$store.state.isPortrait ? window.innerWidth - 72 : 500;
            }
        },
        watch: {
            quoteActive(isActive) {
                let colorHexStr = isActive ? this.barAnimatedColor : this.barIconColor;
                if (isActive) {
                    if (!this.useFallback) {
                        this.drawFreqIcon(colorHexStr);
                        this.audioTimeout = setTimeout(() => {
                            if (isActive) { // make sure the quote is still active when the timeout expires
                                this.playAudio();
                            }
                        }, 300);
                    }
                } else {
                    this.stopAudio();
                    if (!this.useFallback) {
                        this.drawFreqIcon(colorHexStr);
                        clearTimeout(this.audioTimeout);
                    }
                }
            }
        },
        methods: {
            initAnalyser() {
                this.analyser = this.audioCtx.createAnalyser();
                this.analyser.fftSize = this.$store.state.isPortrait ? 64 : 128;
                this.analyser.maxDecibels = 0;
                this.canvasCtx = this.$refs.audioCanvas.getContext('2d');
                this.source = this.audioCtx.createMediaElementSource(this.$refs.audioEmbed);
                this.source.connect(this.analyser);
                this.analyser.connect(this.audioCtx.destination);
                let initBarColor = this.$store.state.isPortrait ? this.barAnimatedColor : this.barIconColor;
                this.drawFreqIcon(initBarColor);
            },
            drawFreqIcon(colorHexStr) {
                this.canvasCtx.clearRect(0, 0, this.$refs.audioCanvas.width, this.$refs.audioCanvas.height);
                this.canvasCtx.fillStyle = colorHexStr;
                let barX = (this.$refs.audioCanvas.width / 2) - ((this.iconBarHeights.length * this.barXBase) / 2);
                for (let i = 0; i < this.iconBarHeights.length; i++) {
                    this.canvasCtx.fillRect(barX, (this.$refs.audioCanvas.height / 2) - (this.iconBarHeights[i] / 2), this.barWidth, this.iconBarHeights[i]);
                    barX += this.barXBase;
                }
            },
            handleListenClick() {
                this.$refs.audioEmbed.play();
                if (this.quoteActive) {
                    this.togglePlay()
                } else {
                    this.$emit('inactiveListenClick', this.quoteIdx);
                }
            },
            togglePlay() {
                this.isPlaying = !this.isPlaying;
                if (this.isPlaying) {
                    this.playAudio();
                } else {
                    this.stopAudio();
                }
            },
            playAudio() {
                if (typeof this.$refs.audioEmbed !== 'undefined') {
                    this.playPromise = this.$refs.audioEmbed.play();
                    if (this.playPromise !== undefined) {
                        this.playPromise.then(() => {
                            if (!this.useFallback) {
                                this.isPlaying = true;
                                this.startAnalyzer();
                            }
                        }).catch(error => {
                        });
                    }
                } else {
                    this.isPlaying = false;
                }
            },
            stopAudio() {
                this.isPlaying = false;
                this.$refs.audioEmbed.pause();
                if (!this.useFallback) {
                    this.stopAnalyzer();
                }
            },
            startAnalyzer() {
                if (!this.rafId) {
                    this.rafId = window.requestAnimationFrame(this.frameLooper);
                }
            },
            stopAnalyzer() {
                if (this.rafId) {
                    window.cancelAnimationFrame(this.rafId);
                    this.rafId = undefined;
                }
            },
            handleAudioEnd() {
                if (!this.useFallback) {
                    this.stopAnalyzer();
                    this.drawFreqIcon(this.barAnimatedColor);
                }
                this.isPlaying = false;
            },
            frameLooper() {
                this.rafId = undefined;
                this.fbc_array = new Uint8Array(this.analyser.frequencyBinCount);
                this.analyser.getByteFrequencyData(this.fbc_array);
                this.canvasCtx.clearRect(0, 0, this.$refs.audioCanvas.width, this.$refs.audioCanvas.height); // Clear the canvas
                this.canvasCtx.fillStyle = this.barAnimatedColor;
                for (let i = 0; i < this.barCount; i++) {
                    this.barHeight = this.fbc_array[i] / 4;
                    if (this.mirrorAnalyser) {
                        let barXRight = (this.$refs.audioCanvas.width / 2) + (i * this.barXBase );
                        let barXLeft = (this.$refs.audioCanvas.width / 2) - (i * this.barXBase );
                        //  fillRect( x, y, width, height ) // Explanation of the parameters below
                        this.canvasCtx.fillRect(barXLeft, (this.$refs.audioCanvas.height / 2) - (this.barHeight / 2), this.barWidth, this.barHeight);
                        this.canvasCtx.fillRect(barXRight, (this.$refs.audioCanvas.height / 2) - (this.barHeight / 2), this.barWidth, this.barHeight);
                    } else {
                        let barX = i * this.barXBase;
                        this.canvasCtx.fillRect(barX, (this.$refs.audioCanvas.height / 2) - (this.barHeight / 2), this.barWidth, this.barHeight);
                    }
                }
                this.startAnalyzer();
            }
        },
        mounted() {
            if (audioContext && !isIosDevice && !isSafari && audioContext.state === 'running') {
                this.initAnalyser();
            } else {
                this.useFallback = true;
            }
        },
        beforeDestroy() {
            this.stopAnalyzer();
        }
    }
</script>

<style lang="scss">
   @import '../scss/the-goods';

   .audio-player {
      width  : 100%;
      margin : 2rem 0 0 0;

      @include screen-mobile {
         position   : absolute;
         bottom     : 9%;
         z-index    : 999;
         opacity    : 0;
         transition : opacity 400ms 500ms $default-bez;
         transform  : translateY(-15px);
      }

      &-canvas {
         height : 50px;
         width  : 100%;
      }

      .portrait-mode & {
         position   : absolute;
         bottom     : 9%;
         z-index    : 999;
         opacity    : 0;
         transition : opacity 400ms 500ms $default-bez;
         transform  : translateY(-15px);
      }

      .landscape-mode & {
         opacity   : 1;
         transform : none;
         position  : relative;
      }
   }

   .visualizer {
      position   : relative;
      height     : 50px;
      max-height : 50px;
      max-width  : 86%;
      margin     : 0 auto;
      text-align : center;

      .landscape-mode & {
         @include screen-xs-sm {
            max-height : 35px;
         }
      }

      img {
         transition : opacity 200ms $default-bez;
         max-height : inherit;
         max-width  : inherit;
      }

      &-still {
         opacity : 1;

         .landscape-mode & {
            @include screen-xs-sm {
               max-height : 22px;
            }
         }

         &.hidden {
            opacity : 0;
         }
      }

      &-gif {
         position  : absolute;
         top       : 0;
         left      : 50%;
         transform : translateX(-50%);
         opacity   : 0;

         &.active {
            opacity : 1;
         }
      }
   }

   .audio-play-pause {
      padding        : 0.5rem 3rem;
      background     : none;
      border         : 0;
      outline        : 0;
      text-transform : capitalize;
      font-weight    : 700;
      font-size      : 1.6rem;
      margin         : -0.5rem 0 0 0;
      transition     : background-color 0.3s ease-out, color 0.3s ease-out;
      position       : relative;
      z-index        : 100;

      &:hover {
         cursor : pointer;
      }

      &:focus {
         outline : none;
      }

      .active & {
         color : white;
      }
   }


</style>



// WEBPACK FOOTER //
// src/components/AudioPlayer.vue